/**
 * @fileoverview Meeting Kit Phase Component
 * @module MeetingKitPhase
 * 
 * Business Context:
 * - Final phase of the Build Your Case wizard
 * - Clean presentation of deliverables
 * - Easy download access with progress tracking
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  Download,
  FileText,
  MessageSquare,
  RefreshCw,
  Loader2,
  Target,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  PartyPopper
} from 'lucide-react';
import { apiClient } from '../../../api/client';
import { AppealSummary, GeneratedArgument } from '../BuildYourCaseWizard';

interface MeetingKitPhaseProps {
  argument: GeneratedArgument | null;
  summary: AppealSummary | null;
  propertyId: string;
  onRegenerate: () => void;
  isRegenerating?: boolean;
  onViewEvidence: () => void;
}

const MeetingKitPhase: React.FC<MeetingKitPhaseProps> = ({
  argument,
  summary,
  propertyId,
  onRegenerate,
  isRegenerating = false,
  onViewEvidence
}) => {
  const [downloadingEvidence, setDownloadingEvidence] = useState(false);
  const [downloadingScript, setDownloadingScript] = useState(false);
  const [downloaded, setDownloaded] = useState<Set<string>>(new Set());
  const [showScript, setShowScript] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const handleDownloadEvidence = async () => {
    try {
      setDownloadingEvidence(true);
      const response = await apiClient.argument.downloadEvidencePacket(propertyId);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Evidence_Packet.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setDownloaded(prev => new Set([...prev, 'evidence']));
    } catch (err: any) {
      console.error('Error downloading evidence packet:', err);
      const msg = err?.response?.data?.error?.message;
      toast.error(msg || 'Failed to download evidence packet. Please try again.');
    } finally {
      setDownloadingEvidence(false);
    }
  };

  const handleDownloadScript = async () => {
    try {
      setDownloadingScript(true);
      const response = await apiClient.argument.downloadPresentationScript(propertyId);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Presentation_Script.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setDownloaded(prev => new Set([...prev, 'script']));
    } catch (err: any) {
      console.error('Error downloading script:', err);
      const msg = err?.response?.data?.error?.message;
      toast.error(msg || 'Failed to download presentation script. Please try again.');
    } finally {
      setDownloadingScript(false);
    }
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!argument) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
        <p className="text-slate-500">No script generated yet.</p>
      </div>
    );
  }

  const script = argument.script;
  const bothDownloaded = downloaded.has('evidence') && downloaded.has('script');

  return (
    <div className="space-y-4">
      {/* Success Header */}
      <div className="bg-teal-600 rounded-2xl p-6 text-center text-white">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
          <PartyPopper className="w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold mb-1">Your Meeting Kit is Ready</h2>
        <p className="text-teal-100 text-sm">
          Download your documents to bring to the assessor meeting
        </p>
      </div>

      {/* Downloads */}
      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
        {/* Evidence Packet */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              downloaded.has('evidence') ? 'bg-teal-50' : 'bg-slate-50'
            }`}>
              <FileText className={`w-5 h-5 ${downloaded.has('evidence') ? 'text-teal-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="font-medium text-slate-900">Evidence Packet</p>
              <p className="text-sm text-slate-500">All your documented evidence in one PDF</p>
            </div>
          </div>
          <button
            onClick={handleDownloadEvidence}
            disabled={downloadingEvidence}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              downloaded.has('evidence')
                ? 'bg-teal-50 text-teal-700'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            {downloadingEvidence ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : downloaded.has('evidence') ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {downloaded.has('evidence') ? 'Downloaded' : 'Download'}
          </button>
        </div>

        {/* Script */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              downloaded.has('script') ? 'bg-teal-50' : 'bg-slate-50'
            }`}>
              <MessageSquare className={`w-5 h-5 ${downloaded.has('script') ? 'text-teal-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="font-medium text-slate-900">Presentation Script</p>
              <p className="text-sm text-slate-500">Your talking points for the meeting</p>
            </div>
          </div>
          <button
            onClick={handleDownloadScript}
            disabled={downloadingScript}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              downloaded.has('script')
                ? 'bg-teal-50 text-teal-700'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            {downloadingScript ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : downloaded.has('script') ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {downloaded.has('script') ? 'Downloaded' : 'Download'}
          </button>
        </div>
      </div>

      {/* Script Preview */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setShowScript(!showScript)}
          className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-600" />
            <span className="font-medium text-slate-700">Preview Script</span>
          </div>
          {showScript ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </button>

        {showScript && (
          <div className="px-5 pb-5 border-t border-slate-100">
            {/* Opening */}
            <div className="py-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Opening</span>
                <button
                  onClick={() => handleCopy(script.openingLine, 'opening')}
                  className="p-1 text-slate-300 hover:text-teal-600 transition-colors"
                >
                  {copied === 'opening' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <p className="text-slate-700 text-sm">{script.openingLine}</p>
            </div>

            {/* Key Points */}
            <div className="py-4 border-b border-slate-100">
              <span className="text-xs font-medium text-slate-400 uppercase">Key Points</span>
              <div className="mt-3 space-y-2">
                {script.keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-slate-700 text-sm">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Closing */}
            <div className="py-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Closing</span>
                <button
                  onClick={() => handleCopy(script.closingLine, 'closing')}
                  className="p-1 text-slate-300 hover:text-teal-600 transition-colors"
                >
                  {copied === 'closing' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <p className="text-slate-700 text-sm">{script.closingLine}</p>
            </div>

            {/* Request */}
            <div className="pt-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-slate-400 uppercase">Your Request</span>
                <p className="text-xl font-bold text-teal-600 mt-1">{script.suggestedReduction}</p>
              </div>
              <Target className="w-8 h-8 text-slate-200" />
            </div>
          </div>
        )}
      </div>

      {/* Ready Message */}
      {bothDownloaded && (
        <div className="bg-emerald-50 rounded-2xl p-5 text-center border border-emerald-100">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="font-semibold text-emerald-800">You're ready for your meeting!</p>
          <p className="text-emerald-600 text-sm mt-1">Good luck with your appeal.</p>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onViewEvidence}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          ← Review evidence
        </button>
        <button
          onClick={onRegenerate}
          disabled={isRegenerating}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
          Regenerate script
        </button>
      </div>
    </div>
  );
};

export default MeetingKitPhase;
